"use client";

import { formatBrandLanguageLabel } from "@/lib/brands";
import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Search, X } from "lucide-react";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";

const MOBILE_PICKER_QUERY = "(max-width: 639px)";

const DEFAULT_POPULAR_VALUES = [
  "es-ES",
  "es-US",
  "fr-FR",
  "de-DE",
  "it-IT",
  "pt-BR",
  "ja-JP",
  "ko-KR",
  "cmn-CN",
  "hi-IN",
  "ru-RU",
  "tr-TR",
  "vi-VN",
  "id-ID",
] as const;

type LanguageEntry = [name: string, code: string];

interface ResponsiveLanguagePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactElement;
  languages: Record<string, string>;
  selectedLanguageCode: string;
  onSelectLanguage: (code: string) => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  title?: string;
  brandVariant?: "default" | "ecole" | "liceo";
  desktopAlign?: "start" | "center" | "end";
  desktopSideOffset?: number;
  popularValues?: readonly string[];
}

function useMobileLanguagePicker() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      setIsMobile(window.innerWidth < 640);
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_PICKER_QUERY);
    const update = () => setIsMobile(mediaQuery.matches);

    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return isMobile;
}

function getLanguageGroups(
  languages: Record<string, string>,
  searchTerm: string,
  popularValues: ReadonlySet<string>
) {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const matchingEntries = Object.entries(languages).filter(([name]) =>
    name.toLowerCase().includes(normalizedSearchTerm)
  );
  const popularEntries: LanguageEntry[] = [];
  const moreEntries: LanguageEntry[] = [];

  for (const entry of matchingEntries) {
    if (popularValues.has(entry[1])) {
      popularEntries.push(entry);
    } else {
      moreEntries.push(entry);
    }
  }

  const sortByName = ([leftName]: LanguageEntry, [rightName]: LanguageEntry) =>
    leftName.localeCompare(rightName);

  popularEntries.sort(sortByName);
  moreEntries.sort(sortByName);

  return { popularEntries, moreEntries };
}

function languageOptionId(listboxId: string, code: string) {
  return `${listboxId}-${code.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

export function ResponsiveLanguagePicker({
  open,
  onOpenChange,
  trigger,
  languages,
  selectedLanguageCode,
  onSelectLanguage,
  searchTerm,
  onSearchTermChange,
  title = "Choose a language",
  brandVariant = "default",
  desktopAlign = "center",
  desktopSideOffset = 8,
  popularValues = DEFAULT_POPULAR_VALUES,
}: ResponsiveLanguagePickerProps) {
  const isMobile = useMobileLanguagePicker();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
  const [mobileViewport, setMobileViewport] = useState<{
    height: number;
    offsetTop: number;
  } | null>(null);
  const popularValueSet = useMemo(() => new Set(popularValues), [popularValues]);
  const { popularEntries, moreEntries } = useMemo(
    () => getLanguageGroups(languages, searchTerm, popularValueSet),
    [languages, popularValueSet, searchTerm]
  );
  const orderedEntries = useMemo(
    () => [...popularEntries, ...moreEntries],
    [moreEntries, popularEntries]
  );
  const isEcole = brandVariant !== "default";

  const handleOpenChange = (nextOpen: boolean) => {
    onSearchTermChange("");
    setActiveOptionIndex(-1);
    onOpenChange(nextOpen);
    if (nextOpen && !isMobile) {
      window.setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  };

  const handleSelection = (code: string) => {
    onSelectLanguage(code);
    handleOpenChange(false);
  };

  const handleMobileSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveOptionIndex((currentIndex) => Math.min(currentIndex + 1, orderedEntries.length - 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveOptionIndex((currentIndex) =>
        currentIndex <= 0 ? orderedEntries.length - 1 : currentIndex - 1
      );
      return;
    }

    if (event.key === "Enter" && activeOptionIndex >= 0) {
      event.preventDefault();
      const activeEntry = orderedEntries[activeOptionIndex];
      if (activeEntry) handleSelection(activeEntry[1]);
    }
  };

  useEffect(() => {
    if (activeOptionIndex < 0) return;
    const activeEntry = orderedEntries[activeOptionIndex];
    if (!activeEntry) return;

    const option = document.getElementById(languageOptionId(listboxId, activeEntry[1]));
    option?.scrollIntoView?.({ block: "nearest" });
  }, [activeOptionIndex, listboxId, orderedEntries]);

  useEffect(() => {
    if (!open || !isMobile || !window.visualViewport) {
      setMobileViewport(null);
      return;
    }

    const visualViewport = window.visualViewport;
    const updateViewport = () => {
      setMobileViewport({
        height: Math.round(visualViewport.height),
        offsetTop: Math.round(visualViewport.offsetTop),
      });
    };

    updateViewport();
    visualViewport.addEventListener("resize", updateViewport);
    visualViewport.addEventListener("scroll", updateViewport);

    return () => {
      visualViewport.removeEventListener("resize", updateViewport);
      visualViewport.removeEventListener("scroll", updateViewport);
    };
  }, [isMobile, open]);

  const renderMobileGroup = (label: string, entries: LanguageEntry[]) => {
    if (entries.length === 0) return null;

    return (
      <section aria-labelledby={`${listboxId}-${label.toLocaleLowerCase().replaceAll(" ", "-")}`}>
        <h3
          id={`${listboxId}-${label.toLocaleLowerCase().replaceAll(" ", "-")}`}
          className="px-4 pb-2 pt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-white/40"
        >
          {label}
        </h3>
        <div className="space-y-1 px-2">
          {entries.map(([name, code]) => {
            const optionIndex = orderedEntries.findIndex(([, entryCode]) => entryCode === code);
            const isSelected = selectedLanguageCode === code;
            const isActive = activeOptionIndex === optionIndex;

            return (
              <button
                key={code}
                id={languageOptionId(listboxId, code)}
                type="button"
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                onPointerMove={() => setActiveOptionIndex(optionIndex)}
                onClick={() => handleSelection(code)}
                className={`flex min-h-12 w-full items-center justify-between rounded-lg border px-4 py-3 text-left text-base font-semibold outline-none transition-colors ${
                  isSelected
                    ? isEcole
                      ? "border-school-accent/45 bg-school-blue/35 text-school-soft"
                      : "border-orange-300/45 bg-orange-500/20 text-orange-100"
                    : isActive
                      ? "border-white/20 bg-white/[0.09] text-white"
                      : "border-transparent text-white/80 hover:bg-white/[0.06] hover:text-white"
                }`}
              >
                <span>{formatBrandLanguageLabel(name, brandVariant)}</span>
                {isSelected && (
                  <span className="text-xs font-bold uppercase tracking-wide">Selected</span>
                )}
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  if (isMobile) {
    const activeEntry = activeOptionIndex >= 0 ? orderedEntries[activeOptionIndex] : undefined;

    return (
      <Dialog.Root open={open} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[10020] bg-black/80" />
          <Dialog.Content
            aria-describedby={undefined}
            data-language-picker-variant="mobile"
            className={`fixed inset-x-0 top-0 z-[10021] flex h-[100dvh] w-screen flex-col overflow-hidden bg-[#0f0f14] text-white outline-none ${
              isEcole ? "selection:bg-school-blue/40" : "selection:bg-orange-500/30"
            }`}
            style={
              mobileViewport
                ? {
                    height: `${mobileViewport.height}px`,
                    top: `${mobileViewport.offsetTop}px`,
                  }
                : undefined
            }
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              mobileCloseButtonRef.current?.focus();
            }}
          >
            <header
              className={`shrink-0 border-b bg-[#0f0f14]/95 px-4 pb-3 pt-[max(env(safe-area-inset-top),1rem)] backdrop-blur-xl ${
                isEcole ? "border-school-blue/30" : "border-white/10"
              }`}
            >
              <div className="mb-3 flex min-h-11 items-center justify-between gap-3">
                <Dialog.Title className="text-xl font-bold tracking-tight text-white">
                  {title}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    ref={mobileCloseButtonRef}
                    type="button"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-white/70 outline-none transition-colors hover:bg-white/[0.08] hover:text-white focus-visible:ring-2 focus-visible:ring-orange-300/60"
                    aria-label="Close language list"
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </Dialog.Close>
              </div>
              <label className="relative block">
                <span className="sr-only">Search languages</span>
                <Search
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/40"
                  aria-hidden="true"
                />
                <input
                  ref={searchInputRef}
                  type="search"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-controls={listboxId}
                  aria-expanded="true"
                  aria-activedescendant={
                    activeEntry ? languageOptionId(listboxId, activeEntry[1]) : undefined
                  }
                  autoComplete="off"
                  enterKeyHint="search"
                  placeholder="Search 70+ languages…"
                  className={`h-14 w-full rounded-lg border bg-white/[0.06] pl-12 pr-4 text-base font-semibold text-white outline-none placeholder:text-white/35 ${
                    isEcole
                      ? "border-school-blue/35 focus:border-school-accent/55"
                      : "border-white/10 focus:border-orange-300/55"
                  }`}
                  value={searchTerm}
                  onChange={(event) => {
                    onSearchTermChange(event.target.value);
                    setActiveOptionIndex(0);
                  }}
                  onKeyDown={handleMobileSearchKeyDown}
                />
              </label>
            </header>

            <div
              id={listboxId}
              role="listbox"
              aria-label="Languages"
              data-language-picker-results=""
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[max(env(safe-area-inset-bottom),1rem)]"
            >
              {orderedEntries.length > 0 ? (
                <>
                  {renderMobileGroup("Popular", popularEntries)}
                  {renderMobileGroup("All languages", moreEntries)}
                </>
              ) : (
                <p
                  role="status"
                  className="px-6 py-12 text-center text-sm font-medium text-white/55"
                >
                  No languages found for “{searchTerm}”.
                </p>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  const renderDesktopGroup = (label: string, entries: LanguageEntry[]) => {
    if (entries.length === 0) return null;

    return (
      <div className="mb-2 last:mb-0">
        <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-white/35">
          {label}
        </div>
        <div className="space-y-1">
          {entries.map(([name, code]) => (
            <DropdownMenu.Item
              key={code}
              onSelect={() => handleSelection(code)}
              className={`flex min-h-11 w-full cursor-pointer items-center justify-between rounded-lg px-4 py-3 text-left text-sm font-semibold outline-none transition-colors ${
                selectedLanguageCode === code
                  ? "bg-white text-black"
                  : "text-white/75 hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white"
              }`}
            >
              <span>{formatBrandLanguageLabel(name, brandVariant)}</span>
              {selectedLanguageCode === code && (
                <span className="text-[10px] font-bold uppercase tracking-wide">Selected</span>
              )}
            </DropdownMenu.Item>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          data-language-picker-variant="desktop"
          className="z-[10020] flex max-h-[min(28rem,calc(100dvh-2rem))] w-[min(20rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-white/10 bg-black/90 text-sm text-white shadow-2xl shadow-black/50 outline-none backdrop-blur-2xl"
          sideOffset={desktopSideOffset}
          align={desktopAlign}
          collisionPadding={16}
        >
          <div className="shrink-0 border-b border-white/10 bg-black/50 p-3 backdrop-blur-md">
            <label className="relative block">
              <span className="sr-only">Search languages</span>
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40"
                aria-hidden="true"
              />
              <input
                ref={searchInputRef}
                type="search"
                autoComplete="off"
                placeholder="Search languages…"
                className="h-12 w-full rounded-lg border border-white/10 bg-white/10 pl-10 pr-3 text-sm font-semibold text-white outline-none placeholder:text-white/40 focus:border-white/25 focus:bg-white/15"
                value={searchTerm}
                onChange={(event) => onSearchTermChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Escape") event.stopPropagation();
                }}
              />
            </label>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
            {orderedEntries.length > 0 ? (
              <>
                {renderDesktopGroup("Popular", popularEntries)}
                {renderDesktopGroup("All languages", moreEntries)}
              </>
            ) : (
              <p role="status" className="px-4 py-8 text-center text-sm font-medium text-white/55">
                No languages found for “{searchTerm}”.
              </p>
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
